import React from "react";
import clsx from "clsx";
import type { AvailableGradesForConfigurableGradeType } from "../../../../../../../shared-logic/types/AvailableInOtherGradesType";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import baseStyles from "./OtherGradeModal.module.scss";
import cardStyles from "../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import {
    calculateTotalPrice,
    checkKeptAndLostItemsWhenChangingGrade,
} from "../../../../../utils/availableInOtherGradesUtils";
import { useCarConfigSelector } from "../../../../../redux/store";
import { CarConfigLabel } from "../../../../../utils/labelUtils";

const GradeCard: React.FC<{
    availableGrade: AvailableGradesForConfigurableGradeType;
    onClickHandler: (availableGrade: AvailableGradesForConfigurableGradeType) => void;
    isActive: boolean;
}> = ({ availableGrade, onClickHandler, isActive }) => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const currentMotorization = useCarConfigSelector((state) => state.car.motorization);
    const basketColour = useCarConfigSelector((state) => state.basket.colour);
    const basketUpholstery = useCarConfigSelector((state) => state.basket.upholstery);
    const basketWheel = useCarConfigSelector((state) => state.basket.wheel);
    const model = useCarConfigSelector((state) => state.car.model);

    const keptAndLostItemsInOtherGrade = checkKeptAndLostItemsWhenChangingGrade(availableGrade, [
        {
            ids: [currentMotorization.engine.id, currentMotorization.transmission.id],
            type: "motorization",
            name: `${currentMotorization.engine.name}, ${currentMotorization.transmission.name}`,
        },
        { ids: [basketColour.id], type: "exteriorColour", name: basketColour.name },
        { ids: [basketUpholstery.id], type: "upholstery", name: basketUpholstery.name },
        { ids: [basketWheel.id], type: "wheel", name: basketWheel.name },
    ]);

    const totalCashPrice = calculateTotalPrice(commonSettings, availableGrade, "cash");
    const totalMonthlyPrice = calculateTotalPrice(commonSettings, availableGrade, "monthly");

    return (
        <article
            className={clsx(cardStyles.card, cardStyles.clickable, isActive && cardStyles.active)}
            onClick={() => onClickHandler(availableGrade)}
        >
            <Text tag="h3" variant="apheleia-h4">
                <Button onClick={() => onClickHandler(availableGrade)} variant="clean" aria-pressed={isActive}>
                    {model.webtitleSectionName}{" "}
                    <Text tag="span" variant="apheleia-h4">
                        {availableGrade.name}
                    </Text>
                </Button>
            </Text>
            <div className={baseStyles["card-lists"]}>
                <div>
                    <Text tag="h4" variant="apheleia-body">
                        <strong>
                            <CarConfigLabel label="carConfigAvailableInOtherGradeYouWillKeep" />
                        </strong>
                    </Text>
                    <ul role="list" className={baseStyles["card-list"]}>
                        {keptAndLostItemsInOtherGrade.kept.map((item) => (
                            <li key={item.shortId}>
                                <Icon variant="check" sizing="xs" aria-hidden="true" />
                                <span>{item.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <Text tag="h4" variant="apheleia-body">
                        <strong>
                            <CarConfigLabel label="carConfigAvailableInOtherGradeYouWillLose" />
                        </strong>
                    </Text>
                    <ul role="list" className={baseStyles["card-list"]}>
                        {keptAndLostItemsInOtherGrade.lost.map((item) => (
                            <li key={item.shortId}>
                                <Icon variant="minus" sizing="xs" aria-hidden="true" />
                                <span>{item.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className={baseStyles["card-footer"]}>
                <div>
                    <Text tag="span" variant="apheleia-price">
                        <CarConfigLabel
                            label="carConfigAvailableInOtherGradeTotalPrice"
                            params={{ price: totalCashPrice }}
                        />
                    </Text>
                    {/*promo, not required yet: */}
                    {/* <Text tag="span" variant="apheleia-body" color="grey-7"> <s>£30,460</s> </Text> */}
                </div>
                {totalMonthlyPrice && (
                    <Text tag="div" variant="apheleia-body" color="grey-7">
                        <CarConfigLabel
                            label="carConfigAvailableInOtherGradeMonthlyPrice"
                            params={{ price: totalMonthlyPrice }}
                        />
                    </Text>
                )}
            </div>
        </article>
    );
};

export default GradeCard;
